// ScenariosTab — Phase 3: Scenario Comparison dashboard tab
// Scenario cards, radar chart, diff table, action buttons
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  PlayArrow as GenerateIcon,
  Add as CustomIcon,
  FileDownload as ExportIcon,
  EmojiEvents as BestIcon,
} from '@mui/icons-material';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import {
  useBomScenarios,
  useBomScenarioComparison,
  useGenerateScenarios,
} from '../../../hooks/useBomIntelligence';
import type { ScenarioSummary } from '../../../types';
import CustomScenarioBuilder from '../components/CustomScenarioBuilder';
import ScenarioExportDialog from '../components/ScenarioExportDialog';
import { colors } from '../../../theme/analyticsColors';

interface ScenariosTabProps {
  bomId: string;
}

const typeColors: Record<string, string> = {
  BASELINE: colors.secondary,
  LOWEST_COST: colors.chart.primary,
  FASTEST_DELIVERY: colors.chart.purple,
  LOWEST_RISK: colors.chart.cyan,
  CUSTOM: colors.chart.warning,
};

const formatCurrency = (val: number) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
  return `$${val.toFixed(0)}`;
};

const ScenariosTab: React.FC<ScenariosTabProps> = ({ bomId }) => {
  const { data: scenarios, isLoading } = useBomScenarios(bomId);
  const generateMutation = useGenerateScenarios();
  const [customOpen, setCustomOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedExportId, setSelectedExportId] = useState('');

  // Get all scenario IDs for comparison
  const scenarioIds = useMemo(
    () => (scenarios || []).map((s) => s.scenarioId),
    [scenarios],
  );

  const { data: comparison } = useBomScenarioComparison(bomId, scenarioIds);

  // Find best values per metric
  const bestMetrics = useMemo(() => {
    if (!scenarios || scenarios.length === 0) return {};
    const metrics = ['totalCost', 'avgLeadTimeDays', 'riskScore', 'savingsVsBaseline'] as const;
    const best: Record<string, string> = {};
    for (const m of metrics) {
      const sorted = [...scenarios].sort((a, b) => {
        if (m === 'savingsVsBaseline') return (b.metrics[m] || 0) - (a.metrics[m] || 0); // higher = better
        return (a.metrics[m] || 0) - (b.metrics[m] || 0); // lower = better
      });
      best[m] = sorted[0]?.scenarioId || '';
    }
    return best;
  }, [scenarios]);

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!scenarios || scenarios.length === 0) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <GenerateIcon sx={{ fontSize: 48, color: colors.chart.primary, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primary, mb: 1 }}>
            No Scenarios Generated Yet
          </Typography>
          <Typography sx={{ color: colors.secondary, mb: 3, maxWidth: 400, mx: 'auto' }}>
            Generate optimized sourcing scenarios to compare cost, delivery, and risk trade-offs for this BOM.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={generateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
            disabled={generateMutation.isPending}
            onClick={() => generateMutation.mutate({ bomId })}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate Scenarios'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={generateMutation.isPending ? <CircularProgress size={14} /> : <GenerateIcon />}
          disabled={generateMutation.isPending}
          onClick={() => generateMutation.mutate({ bomId })}
          sx={{ textTransform: 'none', borderColor: colors.chart.quaternary, color: colors.primary }}
        >
          Regenerate
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CustomIcon />}
          onClick={() => setCustomOpen(true)}
          sx={{ textTransform: 'none', borderColor: colors.chart.quaternary, color: colors.primary }}
        >
          Custom Scenario
        </Button>
      </Box>

      {/* Scenario Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {scenarios.map((scenario) => (
          <Grid key={scenario.scenarioId} size={{ xs: 6, md: 3 }}>
            <ScenarioCard
              scenario={scenario}
              bestMetrics={bestMetrics}
              onExport={() => {
                setSelectedExportId(scenario.scenarioId);
                setExportOpen(true);
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Radar Chart + Diff Table */}
      {comparison && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
              <CardContent>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: colors.primary, mb: 2 }}>
                  Scenario Comparison
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={comparison.radarData}>
                    <PolarGrid stroke={colors.border} />
                    <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11, fill: colors.secondary }} />
                    <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                    {comparison.scenarios.map((s) => (
                      <Radar
                        key={s.scenarioId}
                        name={s.name}
                        dataKey={s.name}
                        stroke={typeColors[s.type] || colors.secondary}
                        fill={typeColors[s.type] || colors.secondary}
                        fillOpacity={0.1}
                        strokeWidth={2}
                      />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
              <CardContent>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: colors.primary, mb: 2 }}>
                  Line Item Differences
                  {comparison.diffItems.length > 0 && (
                    <Chip
                      label={`${comparison.diffItems.length} items differ`}
                      size="small"
                      sx={{ ml: 1, bgcolor: colors.surfaceAlt, color: colors.secondary, fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Typography>
                {comparison.diffItems.length === 0 ? (
                  <Typography sx={{ color: colors.light, fontSize: '0.85rem', textAlign: 'center', py: 4 }}>
                    No differences found between scenarios — all use the same parts.
                  </Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: colors.surfaceAlt }}>
                            Original Part
                          </TableCell>
                          {comparison.scenarios.map((s) => (
                            <TableCell
                              key={s.scenarioId}
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                bgcolor: colors.surfaceAlt,
                                color: typeColors[s.type] || colors.primary,
                              }}
                            >
                              {s.name}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparison.diffItems.slice(0, 20).map((item) => (
                          <TableRow key={item.lineItemId}>
                            <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {item.originalPartNumber}
                            </TableCell>
                            {comparison.scenarios.map((s) => {
                              const sel = item.selections[s.scenarioId];
                              return (
                                <TableCell
                                  key={s.scenarioId}
                                  sx={{
                                    fontSize: '0.7rem',
                                    bgcolor: sel?.isChanged ? alpha(typeColors[s.type] || colors.secondary, 0.06) : 'transparent',
                                  }}
                                >
                                  {sel ? (
                                    <>
                                      <Typography sx={{ fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: sel.isChanged ? 600 : 400 }}>
                                        {sel.selectedPartNumber}
                                      </Typography>
                                      <Typography sx={{ fontSize: '0.6rem', color: colors.light }}>
                                        ${sel.unitCost?.toFixed(2)} | {sel.leadTimeDays}d
                                      </Typography>
                                    </>
                                  ) : '—'}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Dialogs */}
      <CustomScenarioBuilder
        bomId={bomId}
        open={customOpen}
        onClose={() => setCustomOpen(false)}
      />
      <ScenarioExportDialog
        scenarioId={selectedExportId}
        open={exportOpen}
        onClose={() => setExportOpen(false)}
      />
    </Box>
  );
};

// Sub-component: Scenario Card
const ScenarioCard: React.FC<{
  scenario: ScenarioSummary;
  bestMetrics: Record<string, string>;
  onExport: () => void;
}> = ({ scenario, bestMetrics, onExport }) => {
  const m = scenario.metrics;
  const color = typeColors[scenario.type] || colors.secondary;

  const isBestCost = bestMetrics.totalCost === scenario.scenarioId;
  const isBestLead = bestMetrics.avgLeadTimeDays === scenario.scenarioId;
  const isBestRisk = bestMetrics.riskScore === scenario.scenarioId;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: alpha(color, 0.3),
        borderRadius: 2,
        borderTop: `3px solid ${color}`,
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: colors.primary }} noWrap>
            {scenario.name}
          </Typography>
          <Chip
            label={scenario.type.replace(/_/g, ' ')}
            size="small"
            sx={{ bgcolor: alpha(color, 0.1), color, fontSize: '0.6rem', fontWeight: 600, height: 20 }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <MetricRow
            label="Total Cost"
            value={formatCurrency(m.totalCost)}
            isBest={isBestCost}
          />
          <MetricRow
            label="Avg Lead Time"
            value={`${m.avgLeadTimeDays.toFixed(1)}d`}
            isBest={isBestLead}
          />
          <MetricRow
            label="Risk Score"
            value={m.riskScore.toFixed(1)}
            isBest={isBestRisk}
          />
          <MetricRow
            label="Savings"
            value={m.savingsVsBaseline > 0 ? formatCurrency(m.savingsVsBaseline) : '—'}
            positive={m.savingsVsBaseline > 0}
          />
          <MetricRow
            label="Items Changed"
            value={`${m.itemsChanged}`}
          />
        </Box>

        <Button
          size="small"
          startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
          onClick={onExport}
          sx={{ mt: 1.5, textTransform: 'none', fontSize: '0.7rem', color: colors.secondary }}
        >
          Export
        </Button>
      </CardContent>
    </Card>
  );
};

const MetricRow: React.FC<{
  label: string;
  value: string;
  isBest?: boolean;
  positive?: boolean;
}> = ({ label, value, isBest, positive }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography sx={{ fontSize: '0.7rem', color: colors.secondary }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isBest && <BestIcon sx={{ fontSize: 12, color: colors.chart.success }} />}
      <Typography sx={{
        fontSize: '0.8rem',
        fontWeight: 600,
        color: positive ? colors.success : isBest ? colors.chart.success : colors.primary,
      }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export default ScenariosTab;
